<script setup lang="ts">
import { computed, onMounted, watch } from 'vue'
import { useRoute } from 'vue-router'

import AppShell from '@/components/layout/AppShell.vue'
import UiToast from '@/components/ui/UiToast.vue'
import { useToast } from '@/composables/useToast'
import { MOCK_API_ENABLED } from '@/mocks'
import { useAuthStore } from '@/stores/auth'

const route = useRoute()
const auth = useAuthStore()
const toast = useToast()

const bare = computed(() => route.meta.bare === true)

// A permission-denied redirect from the router guard is explained, not swallowed.
watch(
  () => route.query.denied,
  (denied) => {
    if (typeof denied === 'string' && denied) {
      toast.warning(
        'Access denied',
        `That page requires the "${denied}" permission, which your account does not have.`,
      )
    }
  },
  { immediate: true },
)

onMounted(async () => {
  // Refresh permissions on boot so a long-lived tab cannot act on stale RBAC.
  if (auth.isAuthenticated) {
    try {
      await auth.loadCurrentUser()
    } catch {
      /* the API client already handles 401 by ending the session */
    }
  }
})
</script>

<template>
  <div class="app">
    <div v-if="MOCK_API_ENABLED" class="mock-banner" role="status">
      <strong>Mock API — data is synthetic</strong>
      <span>
        This build runs against the in-browser mock layer (<code>VITE_USE_MOCK_API=true</code>).
        Nothing shown here comes from a real camera, edge node or tenant.
      </span>
    </div>

    <RouterView v-if="bare" v-slot="{ Component }">
      <component :is="Component" />
    </RouterView>

    <AppShell v-else>
      <RouterView v-slot="{ Component }">
        <component :is="Component" />
      </RouterView>
    </AppShell>

    <UiToast />
  </div>
</template>

<style scoped>
.app {
  min-height: 100vh;
}

.mock-banner {
  display: flex;
  align-items: center;
  gap: var(--space-3);
  flex-wrap: wrap;
  padding: var(--space-2) var(--space-5);
  background: var(--status-degraded-soft);
  border-bottom: 1px solid var(--status-degraded);
  color: var(--status-degraded-text);
  font-size: var(--text-xs);
  position: sticky;
  top: 0;
  z-index: 90;
}

.mock-banner code {
  font-family: var(--font-mono);
}

.mock-banner span {
  color: var(--text-secondary);
}
</style>
